import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import api from '../utils/api';

const ACTION_COLORS = {
  LOGIN: 'primary', CREATE: 'success', UPDATE: 'warning',
  DELETE: 'danger', ATTENDANCE_CLOCK_IN: 'info', ATTENDANCE_CLOCK_OUT: 'info',
  EXPORT: 'secondary',
};

const NAVY  = 'var(--tmis-heading)';
const TEAL  = 'var(--tmis-teal-btn)';
const MUTED = 'var(--tmis-muted)';
const PAGE_OPTS = [10, 25, 50, 100, 'ALL'];

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ action: '', module: '', search: '', page: 1 });
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState({ by: 'created_at', dir: 'desc' });
  const [flaggedDevices, setFlaggedDevices] = useState([]);
  const [showDevices, setShowDevices] = useState(false);
  const [ipClusters, setIpClusters] = useState([]);
  const [showIpClusters, setShowIpClusters] = useState(false);
  const [hwClusters, setHwClusters] = useState([]);
  const [showHwClusters, setShowHwClusters] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/audit', { params: { ...filters, limit: pageSize, sort_by: sort.by, sort_dir: sort.dir } });
      setLogs(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load audit logs.'); }
    finally { setLoading(false); }
  }, [filters, pageSize, sort]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = pageSize === 'ALL' ? 1 : Math.max(1, Math.ceil(total / pageSize));

  function changePageSize(size) {
    setPageSize(size);
    setFilters(p => ({ ...p, page: 1 }));
  }

  const SORT_COLS = [
    { key: 'created_at', label: 'Time', center: true },
    { key: 'user_email', label: 'User' },
    { key: 'action', label: 'Action', center: true },
    { key: 'module', label: 'Module', center: true },
    { key: 'description', label: 'Description' },
    { key: 'ip_address', label: 'IP', center: true },
  ];

  function toggleSort(key) {
    setSort(p => p.by === key ? { by: key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { by: key, dir: 'asc' });
    setFilters(p => ({ ...p, page: 1 }));
  }

  async function fetchFlagged() {
    try {
      const res = await api.get('/api/devices/flagged');
      setFlaggedDevices(res.data.data || []);
      setShowDevices(true);
    } catch { toast.error('Failed.'); }
  }

  async function fetchIpClusters() {
    try {
      const res = await api.get('/api/devices/ip-clusters');
      setIpClusters(res.data.data || []);
      setShowIpClusters(true);
    } catch { toast.error('Failed.'); }
  }

  async function fetchHwClusters() {
    try {
      const res = await api.get('/api/devices/hardware-clusters');
      setHwClusters(res.data.data || []);
      setShowHwClusters(true);
    } catch { toast.error('Failed.'); }
  }

  function setFilter(k, v) { setFilters(p => ({ ...p, [k]: v, page: 1 })); }

  const ACTIONS = ['LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'ATTENDANCE_CLOCK_IN', 'ATTENDANCE_CLOCK_OUT', 'EXPORT'];
  const MODULES = ['auth', 'home', 'students', 'records', 'reports', 'sessions', 'users', 'permissions'];

  return (
    <div>
      <div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h1>Audit Logs</h1>
          <p>Track all system activity and suspicious devices</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-tmis-outline btn-sm" onClick={fetchFlagged}
            title="Same phone/browser used to clock in for more than one student.">
            <i className="bi bi-shield-exclamation me-1" />Flagged Devices
          </button>
          <button className="btn btn-tmis-outline btn-sm" onClick={fetchIpClusters}
            title="Outdoor sessions only: different students clocking in from the exact same internet address.">
            <i className="bi bi-geo-alt me-1" />IP Clusters (Outdoor)
          </button>
          <button className="btn btn-tmis-outline btn-sm" onClick={fetchHwClusters}
            title="Same phone used with a different browser or app to clock in for another student.">
            <i className="bi bi-cpu me-1" />Hardware Clusters
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tmis-card mb-3 d-flex flex-wrap gap-3 align-items-center">
        <select className="form-select form-select-sm" style={{ width: 160 }} value={filters.action} onChange={e => setFilter('action', e.target.value)}>
          <option value="">All Actions</option>
          {ACTIONS.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ width: 140 }} value={filters.module} onChange={e => setFilter('module', e.target.value)}>
          <option value="">All Modules</option>
          {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className="form-control form-control-sm" style={{ width: 260 }} placeholder="Search by time, user, description, or IP…" value={filters.search} onChange={e => setFilter('search', e.target.value)} />
        <span className="text-muted ms-auto" style={{ fontSize: '0.85rem' }}>{total} log{total !== 1 ? 's' : ''}</span>
      </div>

      <div className="tmis-card">
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: '#17a2b8' }} /></div>
        ) : (
          <div className="table-responsive">
            <table className="table tmis-table mb-0">
              <thead>
                <tr>
                  {SORT_COLS.map(c => (
                    <th key={c.key} className={c.center ? 'text-center' : undefined} onClick={() => toggleSort(c.key)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      {c.label}
                      {sort.by === c.key && <i className={`bi bi-caret-${sort.dir === 'asc' ? 'up' : 'down'}-fill ms-1`} style={{ fontSize: '0.7rem' }} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td className="text-center" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap', color: 'var(--tmis-text)' }}>
                      {moment(l.created_at).format('DD/MM/YY HH:mm:ss')}
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>{l.user_email || '-'}</td>
                    <td className="text-center">
                      <span className={`badge bg-${ACTION_COLORS[l.action] || 'secondary'}`} style={{ fontSize: '0.7rem' }}>
                        {l.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="text-center"><span style={{ fontSize: '0.8rem', color: 'var(--tmis-text)' }}>{l.module}</span></td>
                    <td style={{ fontSize: '0.8rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description || '-'}</td>
                    <td className="text-center" style={{ fontSize: '0.75rem', color: 'var(--tmis-text)' }}>{l.ip_address || '-'}</td>
                  </tr>
                ))}
                {!logs.length && <tr><td colSpan="6" className="text-center py-4 text-muted">No audit logs found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {!loading && total > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-1 pt-3"
            style={{ fontSize: '0.8rem', borderTop: '1px solid var(--tmis-border)', marginTop: 8 }}>
            <div className="d-flex align-items-center gap-2">
              <span style={{ color: MUTED }}>Rows per page:</span>
              {PAGE_OPTS.map(o => (
                <button key={o} onClick={() => changePageSize(o)}
                  style={{
                    padding: '2px 8px', borderRadius: 4, border: '1px solid',
                    borderColor: pageSize === o ? TEAL : 'var(--tmis-border-strong)',
                    background:  pageSize === o ? TEAL : 'var(--tmis-card-bg)',
                    color:       pageSize === o ? 'white' : NAVY,
                    fontWeight:  pageSize === o ? 700 : 400,
                    cursor: 'pointer', fontSize: '0.78rem',
                  }}>
                  {o}
                </button>
              ))}
              <span style={{ color: MUTED, marginLeft: 8 }}>
                {total} log{total !== 1 ? 's' : ''}
              </span>
            </div>
            {pageSize !== 'ALL' && totalPages > 1 && (
              <div className="d-flex align-items-center gap-1">
                <button onClick={() => setFilters(p => ({ ...p, page: 1 }))} disabled={filters.page === 1} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-double-left" /></button>
                <button onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))} disabled={filters.page === 1} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-left" /></button>
                <span style={{ color: MUTED, minWidth: 80, textAlign: 'center' }}>Page {filters.page} of {totalPages}</span>
                <button onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))} disabled={filters.page === totalPages} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-right" /></button>
                <button onClick={() => setFilters(p => ({ ...p, page: totalPages }))} disabled={filters.page === totalPages} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-double-right" /></button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Flagged Devices Modal */}
      {showDevices && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-shield-exclamation me-2 text-warning" />Flagged Devices — Same Phone, Reused</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowDevices(false)} />
              </div>
              <div className="modal-body p-0">
                <p className="px-3 pt-3 text-muted" style={{ fontSize: '0.8rem' }}>
                  Every time a student scans the QR code, we quietly record a code built from that phone's screen,
                  graphics chip, and other traits — not a real fingerprint or photo, nothing biometric. A row below means
                  the <strong>same phone</strong> was used to clock in for more than one student. Clearing the browser's
                  cache or using a private/incognito tab doesn't change this code, so it still catches those attempts.
                </p>
                {flaggedDevices.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-shield-check d-block fs-2 mb-2 text-success" />No flagged devices found.
                  </div>
                ) : (
                  <table className="table tmis-table mb-0">
                    <thead><tr><th className="text-center">Fingerprint</th><th>All Users</th><th className="text-center">Uses</th><th className="text-center">IP</th><th>Reason</th><th className="text-center">Last Seen</th></tr></thead>
                    <tbody>
                      {flaggedDevices.map(d => (
                        <tr key={d.id}>
                          <td className="text-center" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{d.device_fingerprint.substring(0, 16)}…</td>
                          <td style={{ fontSize: '0.8rem' }}>{(d.all_users || [d.first_used_by_email, d.last_used_by_email]).join(', ')}</td>
                          <td className="text-center">{d.usage_count}</td>
                          <td className="text-center" style={{ fontSize: '0.75rem' }}>{d.ip_address}</td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--tmis-absent)' }}>{d.flag_reason}</td>
                          <td className="text-center" style={{ fontSize: '0.75rem', color: 'var(--tmis-text)' }}>{moment(d.updated_at).format('DD/MM/YY HH:mm')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IP Clusters Modal */}
      {showIpClusters && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-geo-alt me-2 text-warning" />IP Clusters — Same Internet Connection (Outdoor Only)</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowIpClusters(false)} />
              </div>
              <div className="modal-body p-0">
                <p className="px-3 pt-3 text-muted" style={{ fontSize: '0.8rem' }}>
                  This one only applies to <strong>outdoor sessions</strong>, which don't require our office WiFi — each
                  student's own mobile data should normally show a different internet address. A row below means several
                  "different" students clocked in from the exact same internet address during the same outdoor session,
                  which can mean one person operating multiple devices, or students sharing a hotspot while off-site.
                  (Indoor sessions aren't checked this way — everyone there shares one office WiFi address anyway.)
                </p>
                {ipClusters.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-shield-check d-block fs-2 mb-2 text-success" />No suspicious IP clusters found.
                  </div>
                ) : (
                  <table className="table tmis-table mb-0">
                    <thead><tr><th className="text-center">Session</th><th className="text-center">Date</th><th className="text-center">IP</th><th>Students</th><th className="text-center">Count</th></tr></thead>
                    <tbody>
                      {ipClusters.map((c, i) => (
                        <tr key={i}>
                          <td className="text-center" style={{ fontSize: '0.8rem' }}>{c.schedule_name}</td>
                          <td className="text-center" style={{ fontSize: '0.8rem' }}>{moment(c.session_date).format('DD/MM/YY')}</td>
                          <td className="text-center" style={{ fontSize: '0.75rem' }}>{c.ip_address}</td>
                          <td style={{ fontSize: '0.8rem' }}>{c.students}</td>
                          <td className="text-center" style={{ fontSize: '0.8rem', color: 'var(--tmis-absent)', fontWeight: 700 }}>{c.student_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hardware Clusters Modal */}
      {showHwClusters && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-cpu me-2 text-warning" />Hardware Clusters — Same Phone, Different Browser</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowHwClusters(false)} />
              </div>
              <div className="modal-body p-0">
                <p className="px-3 pt-3 text-muted" style={{ fontSize: '0.8rem' }}>
                  This catches what "Flagged Devices" can miss: someone switching to a <strong>different browser or app</strong>
                  {' '}on the same phone to clock in for another student. A pair only shows up here if both clock-ins happened
                  in the same session within 90 seconds of each other — a shared phone model alone is never enough, since two
                  students who simply own the same phone would otherwise match with no real evidence. <strong>High</strong> confidence
                  means the browser fingerprint matched too (very likely one device); <strong>Medium</strong> means only the
                  hardware signature matched (still worth a look, but treat as a conversation starter, not proof).
                </p>
                {hwClusters.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-shield-check d-block fs-2 mb-2 text-success" />No matches found.
                  </div>
                ) : (
                  <table className="table tmis-table mb-0">
                    <thead><tr><th>Students</th><th>Session</th><th className="text-center">Time Gap</th><th className="text-center">Confidence</th><th className="text-center">Hardware Signature</th></tr></thead>
                    <tbody>
                      {hwClusters.map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontSize: '0.8rem' }}>{c.student_a} &amp; {c.student_b}</td>
                          <td style={{ fontSize: '0.8rem' }}>{c.schedule_name} ({moment(c.session_date).format('DD/MM/YY')})</td>
                          <td className="text-center" style={{ fontSize: '0.8rem' }}>{c.gap_seconds}s</td>
                          <td className="text-center">
                            <span className={`badge ${c.confidence === 'high' ? 'bg-danger' : 'bg-warning'}`}>
                              {c.confidence === 'high' ? 'High' : 'Medium'}
                            </span>
                          </td>
                          <td className="text-center" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{c.hardware_signature}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
